import {Component, DoCheck, Inject, OnInit} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {CompetitionSimpleDTO} from '../../../../../types/competition';
import {Competitor, CompetitorSimple} from '../../../../../types/competitor';
import {LocalStorageService} from '../../../../../../api/services/localStorage';
import {HttpClient, HttpErrorResponse, HttpHeaders} from '@angular/common/http';
import {CrewSample} from '../../../../../types/crew';
import {CompetitionRegistration} from '../../../../../types/competitionRegistration';
import {AbstractControl, UntypedFormControl, UntypedFormGroup, Validators} from '@angular/forms';
import {HttpHandlingService} from '../../../../../../api/services/http-handling.service';
import {SERVER_API_URL} from '../../../../../types/app.constants';
import {EditCrewCardInfo} from '../../../../../types/editCrewCardInfo';
import {Router} from '@angular/router';

@Component({
  selector: 'app-sign-in-card',
  templateUrl: './sign-in-card.component.html',
  styleUrls: ['./sign-in-card.component.css']
})
export class SignInCardComponent implements OnInit, DoCheck {

  raceSignInForm = new UntypedFormGroup({
    accommodationFrom: new UntypedFormControl('', Validators.required),
    accommodationTo: new UntypedFormControl('', Validators.required),
    crew: new UntypedFormControl('', Validators.required),
    driver: new UntypedFormControl('', Validators.required),
    navigator: new UntypedFormControl('', Validators.required),
    noteCompetitor: new UntypedFormControl(''),
    additionalCompetitor: new UntypedFormControl('')
  });

  competition: CompetitionSimpleDTO;
  crews: CrewSample[];
  allCompetitors: CompetitorSimple[] = [];
  additionalCompetitors: CompetitorSimple[] = [];
  addedCompetitors: CompetitorSimple[] = [];
  drivers: CompetitorSimple[] = [];
  navigators: CompetitorSimple[] = [];

  errorMessage = '';

  serverIsDown = false;
  notRegisteredInSeason = false;
  alreadyInCompetition = false;

  competitionStart: Date | null = null;
  competitionEnd: Date | null = null;

  constructor(@Inject(MAT_DIALOG_DATA) private data: CompetitionSimpleDTO, private http: HttpClient,
              private localStorage: LocalStorageService, private httpHandlingService: HttpHandlingService,
              private dialogRef: MatDialogRef<SignInCardComponent>, private router: Router) {}

  ngOnInit(): void {
    if (this.data !== null) {
      this.competition = this.data;
    }
    if (this.localStorage.get('loggedCompetitor') !== null) {
      const competitor = this.localStorage.get('loggedCompetitor') as Competitor;
      this.onGetCrews(competitor);
    }
    this.competitionStart = new Date(this.competition.competitionStart);
    this.competitionEnd = new Date(this.competition.competitionEnd);
  }

  ngDoCheck(): void {
    this.refilterDropdowns();
  }

  /**
   * function for registration of crew to specific race
   */
  signToCompetition(): void {
    const competitionRegistration = new CompetitionRegistration(null, this.competition.id, this.raceSignInForm.get('crew').value.id,
      this.raceSignInForm.get('accommodationFrom').value, this.raceSignInForm.get('accommodationTo').value, new Date(),
      this.raceSignInForm.get('noteCompetitor').value, '', this.raceSignInForm.get('driver').value.id,
      this.raceSignInForm.get('navigator').value.id, JSON.stringify(this.onGetAdditionalCompetitorsIds(this.addedCompetitors)));
    this.http.post<any>(SERVER_API_URL + '/api/competition-registrations', competitionRegistration,
      {headers: this.httpHandlingService.authorizationHeaders()}).subscribe(result => {},
      (error: HttpErrorResponse) => this.handleCreateErrors(error),
      () => {
        this.dialogRef.close();
        this.router.navigate(['/competition', this.competition.id, 'registered-crews']);
      });
  }

  /**
   * function for getting all crews, where competitor is participating
   * @param competitor for evaluation of competitor and his crews
   */
  onGetCrews(competitor: Competitor): void {
    this.http.get<CrewSample[]>(SERVER_API_URL + '/api/crews-active/' + competitor.id,
      {headers: this.httpHandlingService.authorizationHeaders()}).subscribe((result: CrewSample[]) => {
      this.crews = result;
      if (this.crews.length > 0) {
        this.raceSignInForm.patchValue({
          crew: this.crews[0],
          driver: this.crews[0].driver,
          navigator: this.crews[0].navigator
        });
        this.drivers = this.crews[0].additionalCompetitors;
        this.drivers.push(this.crews[0].driver);
        this.navigators = this.crews[0].additionalCompetitors;
        this.navigators.push(this.crews[0].navigator);
        this.additionalCompetitors = this.crews[0].additionalCompetitors.filter(
          (item: CompetitorSimple) => item.id !== this.crews[0].driver.id && item.id !== this.crews[0].navigator.id);
        this.allCompetitors = this.additionalCompetitors;
        this.allCompetitors.push(this.crews[0].driver, this.crews[0].navigator);
      } else {
        this.crews = [];
      }
    });
  }

  /**
   * function for retrieving additional competitors ids
   * @param additionalCompetitors for evaluation of additional competitors
   */
  onGetAdditionalCompetitorsIds(additionalCompetitors: CompetitorSimple[]): number[] {
    const additionalCompetitorsIds = [];
    for (const additionalCompetitor of additionalCompetitors) {
      additionalCompetitorsIds.push(additionalCompetitor.id);
    }
    return additionalCompetitorsIds;
  }

  /**
   * function for setting default value for dropdown based on comparing ids of competitors
   * @param formControlName for formControlName parameter
   * @param selectedOption for dropdown option values
   */
  compareCompetitors(formControlName: CompetitorSimple, selectedOption: CompetitorSimple): boolean {
    return formControlName && selectedOption && formControlName.id === selectedOption.id;
  }

  /**
   * function for setting default value for dropdown based on comparing numbers of crews
   * @param formControlName for formControlName parameter
   * @param selectedOption for dropdown option values
   */
  compareCrews(formControlName: CrewSample, selectedOption: CrewSample): boolean {
    return formControlName && selectedOption && formControlName.number === selectedOption.number;
  }

  /**
   * function for auto-filling driver and navigator dropdown according to crew
   * @param crew for evaluation of selected crew
   */
  selectDriverAndNavigatorAccordingToCrew(crew: CrewSample): void {
    if (crew) {
      this.additionalCompetitors = crew.additionalCompetitors;
      this.additionalCompetitors.push(crew.driver);
      this.additionalCompetitors.push(crew.navigator);
      this.raceSignInForm.patchValue({
        driver: crew.driver,
        navigator: crew.navigator
      });
    }
  }

  /**
   * function for removing desired competitor from additional competitors list
   * @param competitor for evaluation of competitor to delete
   */
  deleteAdditionalCompetitor(competitor: CompetitorSimple): void {
    this.additionalCompetitors.push(competitor);
    this.addedCompetitors.splice(this.addedCompetitors.indexOf(competitor), 1);
  }

  /**
   * function for disabling add additional competitor, if additional competitor
   * dropdown is empty
   */
  additionalCompetitorFieldIsEmpty(): boolean {
    return this.raceSignInForm.get('additionalCompetitor').value === null || this.raceSignInForm.get('additionalCompetitor').value === '';
  }

  /**
   * function for evaluation of available competitors based on additional competitors
   */
  processAvailableCompetitors(): void {
    const additionalCompetitor = this.raceSignInForm.get('additionalCompetitor').value as CompetitorSimple;
    this.addedCompetitors.push(additionalCompetitor);
    this.additionalCompetitors.splice(this.additionalCompetitors.indexOf(additionalCompetitor), 1);
    this.raceSignInForm.patchValue({additionalCompetitor: null});
  }

  /**
   * function for re-filtering all dropdowns after new input is emitted
   * @param data for input data provided to dialog from start
   */
  refilterDropdowns(): void {
    const driver = this.raceSignInForm.get('driver').value as CompetitorSimple;
    const navigator = this.raceSignInForm.get('navigator').value as CompetitorSimple;
    this.drivers = this.allCompetitors.filter((item: CompetitorSimple) => item.id !== navigator.id &&
      this.addedCompetitors.filter((competitor: CompetitorSimple) => item.id === competitor.id).length === 0);
    this.navigators = this.allCompetitors.filter((item: CompetitorSimple) => item.id !== driver.id &&
      this.addedCompetitors.filter((competitor: CompetitorSimple) => item.id === competitor.id).length === 0);
    this.additionalCompetitors = this.allCompetitors.filter(
      (item: CompetitorSimple) => item.id !== driver.id && item.id !== navigator.id);
  }

  /**
   * function which provides error messages in the process of registration to competition
   * @param fieldControl with information about desired field (crew, driver, navigator)
   */
  getErrorMessage(fieldControl: AbstractControl): string {
    return this.httpHandlingService.handleUserErrorOutputAbstract(fieldControl);
  }

  /**
   * function which provides error messages in the process of registration to competition
   * special function only for datepicker range input
   */
  getErrorMessageRangeDatePicker(): string {
    if (this.raceSignInForm.get('accommodationFrom').invalid) {
      return this.httpHandlingService.handleUserErrorOutputAbstract(this.raceSignInForm.get('accommodationFrom'));
    } else if (this.raceSignInForm.get('accommodationTo').invalid) {
      return this.httpHandlingService.handleUserErrorOutputAbstract(this.raceSignInForm.get('accommodationTo'));
    }
  }

  /**
   * function for handling HTTP errors in the process of crew registration to competition
   */
  handleCreateErrors(error: HttpErrorResponse): void {
    if (error.status === 0) {
      this.serverIsDown = true;
    } else if (error.error.errorKey) {
      switch (error.error.errorKey) {
        case 'notregisteredinactiveseason': this.notRegisteredInSeason = true; break;
        case 'alreadyincompetition': this.alreadyInCompetition = true; this.errorMessage = JSON.parse(error.error.title).error; break;
        default: this.serverIsDown = true; break;
      }
    }
  }
}
